import React, { useCallback, useRef, useEffect, useState, CSSProperties } from 'react';
import clsx from 'clsx';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import CheckIcon from '@material-ui/icons/Check';
import styles from '../FindATempPageV2.module.scss';

export type SectionStatus = 'required' | 'optional' | 'complete' | 'none';

interface AccordionSectionProps {
    number: number;
    title: string;
    status: SectionStatus;
    expanded: boolean;
    onToggle: () => void;
    badge?: React.ReactNode;
    children: React.ReactNode;
    id: string;
}

const statusLabel: Record<SectionStatus, React.ReactNode> = {
    required: <span className={styles.statusRequired}>Required</span>,
    optional: <span className={styles.statusOptional}>Optional</span>,
    complete: (
        <span className={styles.statusComplete}>
            <CheckIcon /> Complete
        </span>
    ),
    none: null,
};

const AccordionSection: React.FC<AccordionSectionProps> = ({
    number,
    title,
    status,
    expanded,
    onToggle,
    badge,
    children,
    id,
}) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const [maxHeight, setMaxHeight] = useState<string>(expanded ? 'none' : '0px');

    useEffect(() => {
        if (expanded) {
            const el = contentRef.current;
            if (el) {
                setMaxHeight(`${el.scrollHeight}px`);
                const timer = setTimeout(() => setMaxHeight('none'), 300);
                return () => clearTimeout(timer);
            }
        } else {
            setMaxHeight('0px');
        }
    }, [expanded]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onToggle();
            }
        },
        [onToggle],
    );

    return (
        <div className={styles.section} id={id}>
            <button
                type="button"
                className={styles.sectionHeader}
                onClick={onToggle}
                onKeyDown={handleKeyDown}
                aria-expanded={expanded ? 'true' : 'false'}
                aria-controls={`${id}-content`}
            >
                <span className={styles.sectionNumber}>{number}</span>
                <h2 className={styles.sectionTitle}>{title}</h2>
                {badge}
                <span className={styles.sectionStatus}>{statusLabel[status]}</span>
                <span className={clsx(styles.chevron, { [styles.chevronOpen]: expanded })}>
                    <KeyboardArrowDownIcon />
                </span>
            </button>
            <div
                id={`${id}-content`}
                role="region"
                aria-labelledby={id}
                className={clsx(styles.sectionContent, { [styles.sectionContentExpanded]: expanded && maxHeight === 'none' })}
                style={{ maxHeight } as CSSProperties}
            >
                <div ref={contentRef} className={styles.sectionContentInner}>
                    {children}
                </div>
            </div>
        </div>
    );
};

export default AccordionSection;
